import { useTimesheetEntryScreen } from './hooks/useTimesheetEntryScreen'
import AppDateInput from '../../../components/AppDateInput'
import AppCombobox from '../../../components/AppCombobox'

function approvalChipClass(status) {
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'REJECTED') return 'bg-error-container text-error'
  return 'bg-primary-fixed text-primary'
}

export default function TimesheetEntryScreen() {
  const { state, actions } = useTimesheetEntryScreen()

  return (
    <div className="mx-auto w-full max-w-[1400px] pb-10">
      <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Timesheet / Timesheet Entry</p>
          <h1 className="mt-2 text-6xl font-extrabold tracking-tight text-on-surface">
            {state.isViewMode ? 'View Timesheet' : state.isEditMode ? 'Edit Timesheet' : 'Add Timesheet'}
          </h1>
        </div>
        {/* <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={actions.handleDiscard}
            className="rounded-md bg-secondary-container px-7 py-3 text-lg font-bold text-on-secondary-container"
          >
            Discard
          </button>
          {!state.isViewMode ? (
            <button
              type="button"
              disabled={state.isSaving || state.isLoading}
              onClick={actions.handleSave}
              className="rounded-md bg-primary px-8 py-3 text-lg font-bold text-on-primary shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              {state.isSaving ? 'Saving...' : state.isEditMode ? 'Save Timesheet' : 'Create Timesheet'}
            </button>
          ) : null}
        </div> */}
      </header>

      {state.error ? (
        <div className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}
      {state.success ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{state.success}</div>
      ) : null}

      {state.isLoading ? (
        <div className="rounded-xl bg-surface-container-low px-6 py-20 text-center text-sm text-on-surface-variant">
          Loading timesheet entry...
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <section className="rounded-3xl bg-surface-container-low p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="material-symbols-outlined rounded-full bg-primary-fixed p-2 text-primary">calendar_month</span>
                <h2 className="text-4xl font-bold tracking-tight text-on-surface">Core Information</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <label className="grid gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Entry Date</span>
                  <AppDateInput
                    value={state.form.entryDate}
                    disabled={state.isViewMode}
                    onChange={(value) => actions.patchForm('entryDate', value)}
                    inputClassName="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm"
                  />
                </label>
                <div className="grid gap-2 md:col-span-2">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                    Job <span className="text-error">*</span>
                  </span>
                  <AppCombobox
                    value={state.form.jobId}
                    disabled={state.isViewMode}
                    onChange={(value) => actions.patchForm('jobId', value)}
                    fetchOptions={actions.fetchJobOptions}
                    selectedLabelOverride={state.mappedWorkName}
                    allowClear={false}
                    minSearchLength={0}
                    placeholder="Search and select job..."
                    searchPlaceholder="Search jobs by name or code..."
                    triggerClassName="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm"
                  />
                </div>
              </div>

              <label className="mt-4 grid gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Activity Description</span>
                <textarea
                  rows={4}
                  disabled={state.isViewMode}
                  value={state.form.activityDescription}
                  onChange={(event) => actions.patchForm('activityDescription', event.target.value)}
                  placeholder="Enter summary of completed work..."
                  className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm outline-none"
                />
              </label>
            </section>

            <section className="rounded-3xl bg-surface-container-low p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="material-symbols-outlined rounded-full bg-primary-fixed p-2 text-primary">schedule</span>
                <h2 className="text-4xl font-bold tracking-tight text-on-surface">Time &amp; Approval</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <label className="grid gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                    Hours <span className="text-error">*</span>
                  </span>
                  <input
                    type="number"
                    min="0"
                    disabled={state.isViewMode}
                    value={state.form.hours}
                    onChange={(event) => actions.patchForm('hours', event.target.value)}
                    className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm outline-none"
                  />
                </label>
                <label className="grid gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Minutes</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    disabled={state.isViewMode}
                    value={state.form.minutes}
                    onChange={(event) => actions.patchForm('minutes', event.target.value)}
                    className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm outline-none"
                  />
                </label>
                <div className="grid gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Approval Status</span>
                  <div className="flex items-center gap-2">
                    {['PENDING', 'APPROVED', 'REJECTED'].map((status) => (
                      <span
                        key={status}
                        className={`rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wider ${
                          state.form.approvalStatus === status
                            ? approvalChipClass(status)
                            : 'bg-surface-container-high text-on-surface-variant'
                        }`}
                      >
                        {status === 'PENDING' ? 'Pending' : status === 'APPROVED' ? 'Approved' : 'Rejected'}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-3xl bg-surface-container-low p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="material-symbols-outlined rounded-full bg-primary-fixed p-2 text-primary">assignment_ind</span>
                <h2 className="text-4xl font-bold tracking-tight text-on-surface">Work Assignment</h2>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Logged By</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-sm font-semibold text-on-surface">
                      {state.metadata.resourceName}
                      <span className="text-xs text-on-surface-variant">{state.metadata.resourceInitials}</span>
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-sm font-semibold text-on-surface">
                      {state.metadata.projectName}
                    </span>
                  </div>
                </div>
                <div className="grid gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Job</span>
                  <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm font-semibold text-on-surface">
                    {state.metadata.jobName}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-3xl bg-surface-container-low p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="material-symbols-outlined rounded-full bg-primary-fixed p-2 text-primary">link</span>
                <h2 className="text-4xl font-bold tracking-tight text-on-surface">Task Mapped</h2>
              </div>
              <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-lg font-semibold text-on-surface">{state.metadata.taskName}</p>
                  <span className="text-sm font-bold text-error">Linked</span>
                </div>
                <p className="mt-1 text-sm text-on-surface-variant">Job: {state.metadata.jobName}</p>
              </div>
            </section>
          </div>

          <div className="col-span-12 space-y-6 lg:col-span-4">
            <section className="rounded-3xl bg-surface-container-low p-6">
              <h3 className="text-4xl font-bold tracking-tight text-on-surface">Timesheet Metadata</h3>
              <div className="mt-6 space-y-5">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Approval</p>
                  <p className="mt-1 text-3xl font-bold text-on-surface">{state.approvalLabel}</p>
                </div>
                <div className="h-px bg-outline-variant/30" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Total Logged</p>
                  <p className="mt-1 text-3xl font-bold text-on-surface">{state.totalLoggedHours.toFixed(2)}h</p>
                </div>
                <div className="h-px bg-outline-variant/30" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-on-surface-variant">Resource</p>
                  <p className="mt-1 text-lg font-semibold text-on-surface">{state.metadata.resourceName}</p>
                </div>
              </div>
            </section>

            <section className="rounded-3xl bg-primary p-6 text-on-primary">
              <h3 className="text-4xl font-bold tracking-tight">Need Help?</h3>
              <p className="mt-3 text-lg text-primary-fixed-dim">
                Use the same structured logging flow as job entry so approvals and billing stay aligned.
              </p>
              <button
                type="button"
                className="mt-6 rounded-full bg-white px-6 py-2.5 text-sm font-bold text-primary"
              >
                Activate AI Help
              </button>
            </section>

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={actions.handleDiscard}
                  className="rounded-md bg-secondary-container px-7 py-3 text-lg font-bold text-on-secondary-container hover:bg-surface-container-highest transition-colors"
                >
                  Discard
                </button>
                {!state.isViewMode ? (
                  <button
                    type="button"
                    disabled={state.isSaving || state.isLoading}
                    onClick={actions.handleSave}
                    className="w-full rounded-md bg-primary px-8 py-3 text-lg font-bold text-on-primary shadow-lg shadow-primary/20 disabled:opacity-50 hover:bg-primary/90 transition-colors"
                  >
                    {(state.isSaving && !state.isMarkingComplete) ? 'Saving...' : state.isEditMode ? 'Save Timesheet' : 'Create Timesheet'}
                  </button>
                ) : null}
              </div>

              {!state.isViewMode ? (
                <button
                  type="button"
                  onClick={actions.handleMarkJobDone}
                  disabled={state.isSaving || state.isLoading}
                  className="w-full rounded-md bg-emerald-600 px-8 py-3 text-lg font-bold text-white shadow-lg shadow-emerald-600/20 disabled:opacity-50 hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                  title="Save timesheet and submit job for manager review"
                >
                  <span className="material-symbols-outlined text-xl">check_circle</span>
                  {state.isMarkingComplete ? 'Submitting for Review...' : 'Mark Job as Done'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
